import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  React.useEffect(() => {
    document.title = 'Terms of Service - Zeus Weather';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_roof-sales-hub/artifacts/8eyuijji_ChatGPT_Image_Jan_28__2026__08_23_42_PM-removebg-preview.png" 
                alt="Zeus Weather" 
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost"
                onClick={() => window.open('https://portal.getzeusweather.com', '_blank')}
                className="text-slate-700 hover:text-slate-900"
              >
                Login
              </Button>
              <Link to="/">
                <Button variant="ghost" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Terms of Service</h1>
          <p className="text-slate-600 mb-8">Last updated: December 29, 2024</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Agreement to Terms</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                By accessing or using Zeus Weather ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Zeus Weather LLC ("Company," "we," "our," or "us") provides storm-damage lead generation software and related services to roofing contractors and storm-response companies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Description of Service</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Zeus Weather provides:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
                <li>Real-time storm tracking and impact mapping</li>
                <li>Property data filtering and lead generation tools</li>
                <li>Mobile applications for field canvassing</li>
                <li>Storm alerts and notifications</li>
                <li>Analytics and reporting features</li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Account Registration</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                To use Zeus Weather, you must:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
                <li>Be at least 18 years old</li>
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain the security of your account credentials</li>
                <li>Be responsible for all activity under your account</li>
                <li>Notify us immediately of any unauthorized access</li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                You may not share your account with others or create multiple accounts.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Subscription and Payment</h2>
              
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Pricing</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Zeus Weather is available on a subscription basis at $349/month or $3,490/year. Pricing is subject to change with 30 days' notice.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-3">Free Trial</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We offer a 30-day free trial for new customers. You will not be charged during the trial period. Your subscription will automatically begin after the trial unless you cancel before the trial ends.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-3">Billing</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
                <li>Subscriptions are billed in advance on a monthly or annual basis</li>
                <li>Payment is due immediately upon subscription or renewal</li>
                <li>Failed payments may result in service suspension</li>
                <li>All fees are non-refundable except as required by law</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-3">Cancellation</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                You may cancel your subscription at any time from your account settings. Cancellations take effect at the end of your current billing period. No refunds will be provided for partial months or unused time.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Acceptable Use</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
                <li>Use the Service for any illegal purpose or in violation of any laws</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>Resell, redistribute, or sublicense access to the Service</li>
                <li>Reverse engineer or attempt to extract source code from the Service</li>
                <li>Use the Service to spam, harass, or send unsolicited communications</li>
                <li>Violate any third-party rights, including privacy and intellectual property rights</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Data Usage and Ownership</h2>
              
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Your Data</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                You retain ownership of any data you input into the Service. By using Zeus Weather, you grant us a license to use your data solely to provide and improve the Service.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-3">Property and Weather Data</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                The property and weather data provided through Zeus Weather is sourced from publicly available databases and authorized data providers. This data is provided "as is" and we make no guarantees about its accuracy, completeness, or timeliness.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mb-3">Usage Restrictions</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                You may use data from Zeus Weather only for your internal business purposes. You may not:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
                <li>Resell or redistribute data obtained through the Service</li>
                <li>Create derivative databases from our data</li>
                <li>Use data for purposes unrelated to storm-response roofing activities</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Intellectual Property</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Zeus Weather and its original content, features, and functionality are owned by Zeus Weather LLC and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Our trademarks, logos, and service marks may not be used without our prior written consent.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Disclaimer of Warranties</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
                <li>Accuracy, reliability, or timeliness of storm and property data</li>
                <li>Uninterrupted or error-free operation</li>
                <li>Fitness for a particular purpose or merchantability</li>
                <li>Results or outcomes from using the Service</li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                You use Zeus Weather at your own risk. We are not responsible for any business decisions, losses, or damages resulting from your use of the Service or reliance on the data provided.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Limitation of Liability</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZEUS WEATHER LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
                <li>Loss of profits, revenue, or business opportunities</li>
                <li>Loss of data or information</li>
                <li>Cost of substitute services</li>
                <li>Business interruption</li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                Our total liability for all claims related to the Service shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Indemnification</h2>
              <p className="text-slate-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Zeus Weather LLC, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the Service or violation of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Termination</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
                <li>Violation of these Terms</li>
                <li>Fraudulent or illegal activity</li>
                <li>Non-payment of fees</li>
                <li>Request by law enforcement or government agencies</li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                Upon termination, your right to use the Service will immediately cease, and we may delete your account and data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Governing Law and Dispute Resolution</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law provisions.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Any disputes arising from these Terms or your use of the Service shall be resolved through binding arbitration, except that either party may seek injunctive relief in court. You waive any right to a jury trial or class action.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Changes to Terms</h2>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify you of material changes by email or through the Service. Your continued use of Zeus Weather after such changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Severability</h2>
              <p className="text-slate-700 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Entire Agreement</h2>
              <p className="text-slate-700 leading-relaxed">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and Zeus Weather LLC regarding the Service and supersede all prior agreements.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Contact Us</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-slate-50 p-6 rounded-lg">
                <p className="text-slate-700 mb-2"><strong>Email:</strong> <a href="mailto:joe@zeusweatherllc.com" className="text-blue-600 hover:text-blue-700">joe@zeusweatherllc.com</a></p>
                <p className="text-slate-700"><strong>Website:</strong> <a href="https://getzeusweather.com" className="text-blue-600 hover:text-blue-700">getzeusweather.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_zeus-roofer-app/artifacts/7vhqji80_logo1-removebg-preview%20%281%29.png" 
                alt="Zeus Weather" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-sm text-slate-400">
                Sales Operating System for Roofing Companies
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/#benefits" className="hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/#pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><a href="https://sites.leadconnectorhq.com/preview/wcmdWIrxpgWpyny0G977" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                <li><a href="https://blog.getzeusweather.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Support</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>&copy; 2025 Zeus Weather. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;